import { a as __extends, b as __awaiter, e as __generator, g as __spread, C as ClientSDKProxyContext } from '../../chunks/chunk-525a49bc.js';
import React from 'react';
import { a as SDK_EXCLUDE_FROM_TRACKING_METHODS } from '../../chunks/chunk-e1ac37a6.js';
import { C as ClientSDKContext } from '../../chunks/chunk-645d2150.js';

// Copyright 2020 Cognite AS
var CacheBase = /** @class */ (function () {
    function CacheBase() {
        this.requests = {};
        this.responses = {};
    }
    CacheBase.prototype.handleFirstCallToCache = function (key) {
        if (this.requests[key] || this.responses[key]) {
            return;
        }
        this.requests[key] = new Map();
        this.responses[key] = new Map();
    };
    CacheBase.prototype.checkCachedValue = function (apiCall, key) {
        var request = this.requests[apiCall].get(key);
        if (request) {
            return { value: request };
        }
        var response = this.responses[apiCall].get(key);
        if (response) {
            return { value: response };
        }
        return null;
    };
    CacheBase.prototype.cacheRequest = function (apiCall, key, request) {
        this.requests[apiCall].set(key, request);
    };
    CacheBase.prototype.cacheResponse = function (apiCall, key, response) {
        this.responses[apiCall].set(key, response);
        this.requests[apiCall].delete(key);
    };
    return CacheBase;
}());

var CacheAssets = /** @class */ (function (_super) {
    __extends(CacheAssets, _super);
    function CacheAssets(client) {
        var _this = _super.call(this) || this;
        _this.retrieve = function (ids) { return __awaiter(_this, void 0, void 0, function () {
            var apiCall, key, cached, request, response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        apiCall = 'retrieve';
                        this.handleFirstCallToCache(apiCall);
                        key = ids
                            .map(function (idObj) {
                            return idObj.id || idObj.externalId;
                        })
                            .sort()
                            .join(',');
                        cached = this.checkCachedValue(apiCall, key);
                        if (cached) {
                            return [2 /*return*/, cached.value];
                        }
                        request = this.client().assets.retrieve(ids);
                        this.cacheRequest(apiCall, key, request);
                        return [4 /*yield*/, request];
                    case 1:
                        response = _a.sent();
                        this.cacheResponse(apiCall, key, response);
                        return [2 /*return*/, response];
                }
            });
        }); };
        _this.client = client;
        return _this;
    }
    return CacheAssets;
}(CacheBase));

var CacheTimeseries = /** @class */ (function (_super) {
    __extends(CacheTimeseries, _super);
    function CacheTimeseries(client) {
        var _this = _super.call(this) || this;
        _this.retrieve = function (ids) { return __awaiter(_this, void 0, void 0, function () {
            var apiCall, key, cached, request, response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        apiCall = 'retrieve';
                        this.handleFirstCallToCache(apiCall);
                        key = ids
                            .map(function (idObj) {
                            return idObj.id || idObj.externalId;
                        })
                            .sort()
                            .join(',');
                        cached = this.checkCachedValue(apiCall, key);
                        if (cached) {
                            return [2 /*return*/, cached.value];
                        }
                        request = this.client().timeseries.retrieve(ids);
                        this.cacheRequest(apiCall, key, request);
                        return [4 /*yield*/, request];
                    case 1:
                        response = _a.sent();
                        this.cacheResponse(apiCall, key, response);
                        return [2 /*return*/, response];
                }
            });
        }); };
        _this.client = client;
        return _this;
    }
    return CacheTimeseries;
}(CacheBase));

var ClientSDKCache = /** @class */ (function () {
    function ClientSDKCache(client) {
        var _this = this;
        this.swapClient = function (client) {
            _this.client = client;
        };
        this.provideClient = function () {
            return _this.client;
        };
        this.client = client;
        this.assets = new CacheAssets(this.provideClient);
        this.timeseries = new CacheTimeseries(this.provideClient);
    }
    return ClientSDKCache;
}());

var wrapInCacheProxy = function (client, cache) {
    var clientHandler = {
        get: function (target, name) {
            if (cache[name]) {
                return new Proxy(cache[name], apiHandler(name));
            }
            else {
                return target[name];
            }
        },
    };
    var apiHandler = function (name) { return ({
        get: function (target, propKey) {
            return target[propKey]
                ? target[propKey]
                : client[name][propKey];
        },
    }); };
    return new Proxy(client, clientHandler);
};

// Copyright 2020 Cognite AS
var version = '2.2.2';

var GearboxHeader = "CogniteGearbox:" + version;
function getComponentHeader(component) {
    return GearboxHeader + "/" + component;
}
function wrapInLogProxy(client, componentName) {
    var clientHandler = {
        get: function (target, name) {
            if (target[name] &&
                !SDK_EXCLUDE_FROM_TRACKING_METHODS.includes(name)) {
                return new Proxy(target[name], createApiHandler());
            }
            else {
                return target[name];
            }
        },
    };
    var createApiHandler = function () { return ({
        get: function (target, propKey) {
            var originalProperty = target[propKey];
            if (typeof originalProperty === 'function') {
                return function () {
                    var args = [];
                    for (var _i = 0; _i < arguments.length; _i++) {
                        args[_i] = arguments[_i];
                    }
                    if (client.setOneTimeSdkHeader) {
                        client.setOneTimeSdkHeader(getComponentHeader(componentName));
                    }
                    return originalProperty.apply(void 0, __spread(args));
                };
            }
            else {
                return originalProperty;
            }
        },
    }); };
    return client ? new Proxy(client, clientHandler) : client;
}

// Copyright 2020 Cognite AS
function wrapInProxies(client) {
    var sdkCache = client ? new ClientSDKCache(client) : client;
    return function (component, cache) {
        if (cache === void 0) { cache = false; }
        var sdkClient = client ? wrapInLogProxy(client, component) : client;
        if (cache && sdkCache && sdkClient) {
            sdkCache.swapClient(sdkClient);
            return wrapInCacheProxy(sdkClient, sdkCache);
        }
        return sdkClient;
    };
}

// Copyright 2020 Cognite AS
var ClientSDKProxyProvider = function (_a) {
    var client = _a.client, children = _a.children;
    return (React.createElement(ClientSDKProxyContext.Provider, { value: wrapInProxies(client) }, children));
};

// Copyright 2020 Cognite AS
var ClientSDKProvider = function (_a) {
    var client = _a.client, children = _a.children;
    return (React.createElement(ClientSDKContext.Provider, { value: client },
        React.createElement(ClientSDKProxyProvider, { client: client }, children)));
};

export { ClientSDKProvider };
